// src/components/ModalManifestoNfe.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";

export default function ModalNotasManifestadas({ empresaId, open, onClose, token }) {
  const [items, setItems] = useState([]);
  const [filtro, setFiltro] = useState("todas"); // todas|pendentes|confirmadas|24h|7d
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(false);

  const authHeader = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  function asDate(val) {
    if (!val) return null;
    if (val.toDate) return val.toDate();
    if (typeof val === "object" && typeof val.seconds === "number") return new Date(val.seconds * 1000);
    if (typeof val === "number") return new Date(val);
    const t = Date.parse(val);
    return Number.isNaN(t) ? null : new Date(t);
  }

  function tipoLabel(t) {
    const s = String(t || "").toLowerCase();
    if (s === "ciencia") return "Ciência";
    if (s === "confirmacao") return "Confirmação";
    if (s === "desconhecimento") return "Desconhecimento";
    if (s === "nao_realizada" || s === "nao-realizada") return "Não realizada";
    return t || "";
  }

  const abortRef = useRef(null);

  const carregar = useCallback(async () => {
    if (!empresaId) return;
    try {
      setLoading(true);
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const qs = [];
      if (filtro === "pendentes") qs.push("confirmado=0");
      if (filtro === "confirmadas") qs.push("confirmado=1");
      if (filtro === "24h") qs.push(`desde=${new Date(Date.now() - 24 * 3600e3).toISOString()}`);
      if (filtro === "7d") qs.push(`desde=${new Date(Date.now() - 7 * 24 * 3600e3).toISOString()}`);

      const url = `${API_URL}/api/focus/manifestos/${empresaId}${qs.length ? `?${qs.join("&")}` : ""}`;
      const r = await fetch(url, { headers: authHeader, signal: ac.signal });

      if (!r.ok) {
        console.warn("Falha ao carregar manifestos:", r.status);
        setItems([]);
      } else {
        const data = await r.json();
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        console.warn("Erro ao carregar manifestos:", e);
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [empresaId, filtro, authHeader]);

  useEffect(() => {
    if (open) carregar();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [open, carregar]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.chave || "").toLowerCase().includes(q) ||
      (it.emitente_nome || "").toLowerCase().includes(q) ||
      (it.cnpj_emitente || "").includes(q)
    );
  }, [items, busca]);

  const refresh = useCallback(
    async (chave) => {
      try {
        if (!empresaId) return;
        await fetch(`${API_URL}/api/focus/manifestos/${empresaId}/${chave}/refresh`, { headers: authHeader });
      } catch (e) {
        console.warn("Erro ao pedir refresh do manifesto:", e);
      } finally {
        await carregar();
      }
    },
    [empresaId, authHeader, carregar]
  );

  async function abrirDanfe(chave) {
    try {
      const url = new URL(`${API_URL}/api/focus/baixar-danfe/${chave}`);
      url.searchParams.set("empresaId", empresaId);
      const r = await fetch(url.toString(), { headers: authHeader });
      if (!r.ok) throw new Error(`Falha ao baixar DANFE (${r.status})`);
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.target = "_blank";
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (e) {
      console.warn(e);
      alert(e.message || "Falha ao abrir DANFE.");
    }
  }

  async function baixarXml(chave) {
    try {
      const url = new URL(`${API_URL}/api/focus/baixar-xml/${chave}`);
      url.searchParams.set("empresaId", empresaId);
      const r = await fetch(url.toString(), { headers: authHeader });
      if (!r.ok) throw new Error(`Falha ao baixar XML (${r.status})`);
      const text = await r.text();
      const isResumo = /^\s*<\s*resNFe\b/i.test(text);
      if (isResumo) {
        alert("A SEFAZ/Focus ainda está entregando apenas o resumo (resNFe). Tente 'Rever status' e atualize depois para obter o XML completo.");
      }
      const blob = new Blob([text], { type: "application/xml" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${chave}.xml`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (e) {
      console.warn(e);
      alert(e.message || "Falha ao baixar XML.");
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fadein">
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b">
          <h2 className="text-xl md:text-2xl font-bold">Notas manifestadas</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-gray-600 hover:text-black hover:bg-gray-100 transition"
          >
            Fechar
          </button>
        </div>

        {/* TOOLBAR */}
        <div className="px-6 pt-4 pb-3 border-b bg-white">
          <div className="flex flex-col md:flex-row gap-2">
            <select
              className="rounded-lg border px-3 py-2 text-sm md:text-base hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
            >
              <option value="todas">Todas</option>
              <option value="pendentes">Pendentes</option>
              <option value="confirmadas">Confirmadas</option>
              <option value="24h">Últimas 24h</option>
              <option value="7d">Últimos 7 dias</option>
            </select>

            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 21l-4.3-4.3m1.3-4.2A7 7 0 1 1 7 5a7 7 0 0 1 11 7.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                placeholder="Buscar por chave, emitente ou CNPJ"
                className="w-full rounded-lg border pl-9 pr-3 py-2 text-sm md:text-base hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </div>

            <button
              onClick={carregar}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-4 py-2 text-white font-medium hover:bg-gray-900 disabled:opacity-60"
              title="Atualizar lista"
            >
              {loading && (
                <span className="inline-block h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
              )}
              {loading ? "Atualizando..." : "Atualizar"}
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          <div className="mt-4 overflow-hidden rounded-2xl border">
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white shadow-sm">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold">Chave</th>
                    <th className="px-4 py-3 font-semibold">Tipo</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Solicitado em</th>
                    {/* Ações centralizado */}
                    <th className="px-4 py-3 font-semibold text-center w-[340px] sm:w-[380px] whitespace-nowrap">
  Ações
</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && items.length === 0 && (
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={`s-${i}`} className="border-t">
                        <td className="px-4 py-3"><div className="h-4 w-64 bg-gray-200/70 rounded animate-pulse" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-24 bg-gray-200/70 rounded animate-pulse" /></td>
                        <td className="px-4 py-3"><div className="h-6 w-24 bg-gray-200/70 rounded-full animate-pulse" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-40 bg-gray-200/70 rounded animate-pulse" /></td>
                        <td className="px-4 py-3 text-center"><div className="h-8 w-40 mx-auto bg-gray-200/70 rounded-full animate-pulse" /></td>
                      </tr>
                    ))
                  )}

                  {!loading && filtrados.map((it, idx) => {
                    const solicitadoEm = asDate(it.solicitado_em);

                    return (
                      <tr key={it.id || it.chave || idx} className="border-t odd:bg-gray-50/60 hover:bg-gray-100/50 transition">
                        <td className="px-4 py-3 align-top">
                          <div className="font-mono text-[13px] leading-5 break-words">{it.chave}</div>
                          {it.emitente_nome && (
                            <div className="text-xs text-gray-500">
                              {it.emitente_nome} {it.cnpj_emitente ? `(${it.cnpj_emitente})` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">{tipoLabel(it.tipo)}</td>
                        <td className="px-4 py-3 align-top">
                          {it.confirmado ? (
                            <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2.5 py-1 text-xs font-medium">
                              Confirmado
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2.5 py-1 text-xs font-medium">
                              Pendente
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">{solicitadoEm ? solicitadoEm.toLocaleString() : "—"}</td>

                        {/* AÇÕES COLORIDAS + CENTRALIZADAS */}
                        <td className="px-4 py-3 align-top">
  <div className="flex items-center justify-center gap-2 flex-nowrap whitespace-nowrap">
                            <button
                              onClick={() => refresh(it.chave)}
                              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 shadow-sm"
                              title="Forçar rechecagem na Focus"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                <path d="M4 7h6M4 7l3-3m-3 3l3 3M20 17h-6m6 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="hidden sm:inline">Rever status</span>
                            </button>

                            <button
                              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 shadow-sm"
                              onClick={() => abrirDanfe(it.chave)}
                              title="Abrir DANFE (PDF)"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="currentColor" strokeWidth="2"/>
                                <path d="M14 3v4h4" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              <span className="hidden sm:inline">DANFE</span>
                            </button>

                            <button
                              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium bg-slate-100 text-slate-800 hover:bg-slate-200 shadow-sm"
                              onClick={() => baixarXml(it.chave)}
                              title="Baixar XML"
                            >
                              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                                <path d="M7 8l-4 4 4 4M17 8l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="hidden sm:inline">XML</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!loading && filtrados.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                        Nenhum registro encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-fadein { animation: fadein .25s both; }
        @keyframes fadein { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </div>
  );
}
